import { useNavigate } from 'react-router-dom'
import { useMarkNotificationRead } from '@/features/notifications/use-notifications'
import { canonicalProjectUrl } from '@/lib/canonical-url'
import { cn } from '@/lib/utils'
import type { Notification } from '@/api/types'

interface NotificationItemProps {
  notification: Notification
  /** When true, render the compact popover row; otherwise the full inbox row. */
  compact?: boolean
}

const formatRelative = (iso: string): string => {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
  if (seconds < 60) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 14) return `${days}d ago`
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const verbFor = (eventType: Notification['eventType']): string => {
  switch (eventType) {
    case 'PROJECT_PUBLISHED':
      return 'published'
    case 'INVITE_RECEIVED':
      return 'invited you to'
    case 'INVITE_ACCEPTED':
      return 'accepted your invite to'
    case 'INVITE_DECLINED':
      return 'declined your invite to'
    case 'INVITE_REVOKED':
      return 'revoked your access to'
    case 'ROLE_CHANGED':
      return 'changed your role in'
    default:
      return ''
  }
}

/**
 * Phase 14 — one notification row, used in both the navbar popover (compact) and the full
 * inbox page. Click marks read + navigates to the project's canonical URL.
 */
export const NotificationItem = ({ notification, compact = false }: NotificationItemProps) => {
  const navigate = useNavigate()
  const markRead = useMarkNotificationRead()
  const isRead = notification.readAt != null
  const project = notification.project
  const actor = notification.actor

  const handleClick = () => {
    if (!isRead) {
      markRead.mutate(notification.id)
    }
    if (project && actor) {
      navigate(canonicalProjectUrl(actor.handle, project.slug))
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'flex w-full items-start gap-3 border-b border-border px-3 py-2 text-left transition-colors hover:bg-accent/40',
        !isRead && 'bg-primary/5',
        compact ? 'text-xs' : 'text-sm'
      )}
    >
      {!isRead && (
        <span
          className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-primary"
          aria-label="Unread"
        />
      )}
      {actor?.avatarUrl ? (
        <img
          src={actor.avatarUrl}
          alt=""
          className={cn('shrink-0 rounded-full object-cover', compact ? 'h-6 w-6' : 'h-8 w-8')}
        />
      ) : (
        <div
          className={cn('shrink-0 rounded-full bg-muted', compact ? 'h-6 w-6' : 'h-8 w-8')}
          aria-hidden
        />
      )}
      <div className="min-w-0 flex-1">
        <div className={cn('truncate', isRead ? 'text-muted-foreground' : 'text-foreground')}>
          <span className="font-medium">@{actor?.handle ?? '(unknown)'}</span>{' '}
          <span className="text-muted-foreground">{verbFor(notification.eventType)}</span>{' '}
          <span className="font-medium">{project?.title ?? '(deleted project)'}</span>
        </div>
        <div className="text-xs text-muted-foreground">
          {formatRelative(notification.createdAt)}
        </div>
      </div>
    </button>
  )
}
